import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import * as R from "ramda";
import { Box, Heading, RadioGroup } from "@chakra-ui/react";

import { isWallet, makePMTitleMap } from "components/PaymentMethods/common";

import { noop, parsePMIDFromFlowState, useSessionCtx } from "utils";
import { POSLegalese } from "components/Legalese";
import PaymentMethodItem from "./PaymentMethodItem";
import AddPaymentMethodButtons from "../AddPaymentMethodButtons";
import { getFinalLabelWithDefault, buttonLabelByFlow, SelectButton } from "../SelectButton";

export const sectionHeaderMap = {
  payout: "Choose your payout method",
  purchase: "Choose your purchase method",
};

/**
 * Based on logic in <SectionHeader />
 */
export const translateHeaderText = (
  { flow, flowConfig = {}, defaultMessage = null },
  formatMessage
) => {
  const labelConfig = R.path(["section", "label"], flowConfig);
  if (labelConfig === null) return null;
  if (labelConfig) return labelConfig;

  switch (flow) {
    case "payout": {
      return formatMessage({
        defaultMessage: "Choose your payout method",
        description: "Header title",
      });
    }
    case "purchase": {
      return formatMessage({
        defaultMessage: "Choose your purchase method",
        description: "Header title",
      });
    }
    default: {
      return (
        defaultMessage ||
        formatMessage({
          defaultMessage: "Choose your payout method",
          description: "Header title",
        })
      );
    }
  }
};

// flowConfig.section.label:
//   - null: no header
//   - undefined: default header text
//   - <other value>: header with other value
export const SectionHeader = ({ flow, flowConfig = {} }) => {
  const { formatMessage } = useIntl();
  const labelConfig = R.path(["section", "label"], flowConfig);
  if (labelConfig === null) return null;

  return (
    <Heading mb={2} mt={3}>
      {translateHeaderText(
        {
          flow,
          flowConfig,
          defaultMessage: formatMessage({
            defaultMessage: "Choose your payment method",
            description: "Header title",
          }),
        },
        formatMessage
      )}
    </Heading>
  );
};

const findById = (id, data) => R.find(R.propEq("id", id))(data);

export const PMList = ({
  paymentMethods = [],
  chooserConfig = {},
  allowedProviders = {},
  onSelect = noop,
  onReady = noop,
  showMenu,
  onComplete = noop,
}) => {
  const { formatMessage } = useIntl();
  useEffect(onReady, [onReady]);
  const { debug, sessionCtx } = useSessionCtx();
  const { flow, flowState, flowConfig } = sessionCtx;
  debug("PMList__flowConfig: ", flowConfig);

  // flow state may have a PM to initially select (after a create)
  const initialSelectedPM = parsePMIDFromFlowState(flowState);
  const [selected, setSelected] = useState(initialSelectedPM);

  const selectButtonLabel = getFinalLabelWithDefault(
    flowConfig,
    buttonLabelByFlow(flow, formatMessage)
  );
  const titleMap = makePMTitleMap(formatMessage);
  const addPMTitle = titleMap[flow] || formatMessage({
    defaultMessage: "Add a payment method to your account",
    description: "Button label"
  });

  return (
    <Box>
      <SectionHeader flow={flow} flowConfig={flowConfig} />

      <RadioGroup onChange={setSelected} value={selected}>
        {paymentMethods.map((pm) => (
          <PaymentMethodItem
            key={`payment-method-item-${pm.id}`}
            data={pm}
            showMenu={showMenu && !isWallet(pm)}
            onComplete={onComplete}
          />
        ))}
      </RadioGroup>

      <Box mt={4} mb={8}>
        <SelectButton
          label={selectButtonLabel}
          chooserConfig={chooserConfig}
          flowConfig={flowConfig}
          disabled={selected === null}
          onClick={() => onSelect({ state: "complete", ...findById(selected, paymentMethods) })}
        />
      </Box>

      <AddPaymentMethodButtons
        title={addPMTitle}
        chooserConfig={chooserConfig}
        allowedProviders={allowedProviders}
        onCancel={() => onSelect({ state: "cancel" })}
      />

      <POSLegalese flow={flow} buttonLabel={selectButtonLabel} />
    </Box>
  );
};
